/**
 * API Service for HIVA WebChat Widget
 */

import type {
  VisitorInfo,
  StartConversationResponse,
  SendMessageRequest,
  SendMessageResponse,
  Message
} from '../types';

export class WebChatAPI {
  private baseUrl: string;
  private integrationId: string;

  constructor(baseUrl: string, integrationId: string) {
    this.baseUrl = baseUrl.replace(/\/$/, ''); // Remove trailing slash
    this.integrationId = integrationId;
  }

  /**
   * Start a new conversation
   */
  async startConversation(visitorInfo: VisitorInfo, initialMessage?: string): Promise<StartConversationResponse> {
    const response = await fetch(`${this.baseUrl}/api/v1/webchat/conversations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        integration_id: this.integrationId,
        visitor_info: visitorInfo,
        initial_message: initialMessage,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to start conversation' }));
      throw new Error(error.detail || 'Failed to start conversation');
    }

    return response.json();
  }

  /**
   * Send a message in the conversation
   */
  async sendMessage(
    conversationId: string,
    sessionToken: string,
    message: SendMessageRequest
  ): Promise<SendMessageResponse> {
    const response = await fetch(
      `${this.baseUrl}/api/v1/webchat/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`,
        },
        body: JSON.stringify(message),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to send message' }));
      throw new Error(error.detail || 'Failed to send message');
    }

    return response.json();
  }

  /**
   * Get message history
   */
  async getMessages(
    conversationId: string,
    sessionToken: string,
    limit = 50,
    offset = 0
  ): Promise<{ messages: Message[]; total: number; has_more: boolean }> {
    const response = await fetch(
      `${this.baseUrl}/api/v1/webchat/conversations/${conversationId}/messages?limit=${limit}&offset=${offset}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to get messages' }));
      throw new Error(error.detail || 'Failed to get messages');
    }

    return response.json();
  }
}
